import { isNull, isUndefined } from 'lodash/fp';
import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';

import { useAppDispatch } from '../../../hooks/react-redux';
import { IconButton } from '../../shared/button/IconButton';
import { Delete, QuestionMark, Tick } from '../../shared/icons';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../shared/tooltip';
import styles from './NettingEngine.module.scss';
import { NettingEngineJurisdictionResultsSection } from './NettingEngineJurisdictionResultsSection';
import { ISDAJurisdictionalNettingInformation, ProductTypeCoveredEnum, setSelectedProductCovered, JurisdictionalNettingDetailsAnswer, GMRAJurisdictionalNettingInformation } from './store';

const { green, red, amber } = styles;

// We are rolling back changes to transaction coverage for now until the data is caught up for all opinions
const productsCoveredDataUpToDate = false;

interface NettingEngineJurisdictionProductsCoveredProps {
    jurisdictionalResults: ISDAJurisdictionalNettingInformation | GMRAJurisdictionalNettingInformation;
    viewCustomConclusions: boolean;
    setTransactionConditionModalOpen: Dispatch<SetStateAction<boolean>>
    getAnswerJSX: (question: string, answer: JurisdictionalNettingDetailsAnswer, noPositive?: boolean, isLocalLaw?: boolean, aetApplicable?: string | undefined) => JSX.Element;
}

export const NettingEngineJurisdictionProductsCovered: React.FC<NettingEngineJurisdictionProductsCoveredProps> = ({ jurisdictionalResults, viewCustomConclusions, setTransactionConditionModalOpen, getAnswerJSX }) => {
    const dispatch = useAppDispatch();

    const { details, clientSignOffDetails } = jurisdictionalResults;

    const customConclusionsExist = useMemo(() => !isNull(clientSignOffDetails) && viewCustomConclusions, [clientSignOffDetails, viewCustomConclusions]);

    const { productsCovered } = customConclusionsExist ? clientSignOffDetails! : details;

    const handleIconClick = useCallback((label: string) => {
        const selectedProductType = productsCovered.find(({ product }) => product === label);
        if (!isUndefined(selectedProductType)) {
            dispatch(setSelectedProductCovered(selectedProductType));
        }
        setTransactionConditionModalOpen(true);
    }, [productsCovered, dispatch, setTransactionConditionModalOpen]);

    const getIconColor = useCallback((covered: ProductTypeCoveredEnum, conditions?: string) => {
        if (productsCoveredDataUpToDate) {
            if ([ProductTypeCoveredEnum.NO, ProductTypeCoveredEnum.UNCLEAR].includes(covered)) {
                return amber;
            }
            if (conditions === 'Yes') {
                return green;
            }
            if (conditions === 'No') {
                return red;
            }
            return amber;
        }
        switch (covered) {
            case ProductTypeCoveredEnum.YES:
                return green;
            case ProductTypeCoveredEnum.NO:
                return red;
            case ProductTypeCoveredEnum.UNCLEAR:
            default:
                return amber;
        }
    }, []);

    const getIconForProduct = useCallback((covered: ProductTypeCoveredEnum, conditions?: string) => {
        if (productsCoveredDataUpToDate) {
            if ([ProductTypeCoveredEnum.NO, ProductTypeCoveredEnum.UNCLEAR].includes(covered) || isUndefined(conditions)) {
                return QuestionMark;
            }
            if (covered === ProductTypeCoveredEnum.YES) {
                if (['Yes', 'Yes - with exceptions'].includes(conditions)) {
                    return Tick;
                }
                if (['No', 'No - with exceptions'].includes(conditions)) {
                    return Delete;
                }
                if (['Unclear', 'Not applicable'].includes(conditions)) {
                    return QuestionMark;
                }
            }
            return QuestionMark;
        }
        switch (covered) {
            case ProductTypeCoveredEnum.YES:
                return Tick;
            case ProductTypeCoveredEnum.NO:
                return Delete;
            case ProductTypeCoveredEnum.UNCLEAR:
            default:
                return QuestionMark;
        }
    }, []);

    const productsCoveredElement = useMemo(() => (
        <div className={styles.productsCoveredWrapper}>
            <Scrollable maxHeight='15vh' width='calc(100% - 2px)'>
                <div className={styles.productColumn}>
                    {productsCovered.map(({ product, covered, conditions }, index) => <div className={styles.productsCovered} key={index}>
                        <OverflowTooltip className={styles.product} overlayText={product} />
                        <div className={styles.covered}>
                            <IconButton
                                icon={getIconForProduct(covered, conditions)}
                                color={getIconColor(covered, conditions)}
                                onClick={() => handleIconClick(product)}
                            />
                        </div>
                    </div>)}
                </div>
            </Scrollable>
        </div>
    ), [getIconColor, getIconForProduct, handleIconClick, productsCovered]);

    const productInformationNotInArk = useMemo(() => (
        <div className={styles.productsCoveredWrapper}>
            <div className={styles.productInformationNotInArk}>
                <div className={styles.answer}>{getAnswerJSX('', { value: 'Information not in Ark', details: null })}</div>
            </div>
        </div>
    ), [getAnswerJSX]);

    const productsCoveredAnswer = useMemo(() => productsCovered.length > 0 ? productsCoveredElement : productInformationNotInArk, [productsCovered.length, productsCoveredElement, productInformationNotInArk]);
    const question = useMemo(() => `Transaction types Covered ${productsCovered.length > 0 ? '(click icons for details)' : ''}`, [productsCovered.length]);

    return <NettingEngineJurisdictionResultsSection label={question} children={productsCoveredAnswer} />;
};
